import React, { useEffect, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import {
  createChargeRule,
  createProduct,
  createSavedAddress,
  createService,
} from "../../../graphql/mutations";
import {
  sanitizeAccount,
  syncableAccountCreateProps,
} from "../../../graphql/account/accountCRUD";

import { Backdrop, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Confirm,
  FormDataConsumer,
  SaveButton,
  useNotify,
  useRedirect,
} from "react-admin";
import { useForm } from "react-final-form";

import { getCustomFieldId } from "../../../components/common/CustomFields";
import { SaveIcon, UploadIcon } from "../../../components/common/iconography";

import { logError, logHistory } from "../../../utils/appUtils";
import {
  AccountState,
  AccountStatus,
  ExternalStatus,
  FilePaths,
  InventoryStatus,
  ResourceType,
  Unit,
  UnitToColumn,
} from "../../../utils/constants";
import { saveRemoveFile, saveRemoveFiles } from "../../../utils/fileUtils";
import {
  fetchTriggerEnabled,
  getTriggerEnabled,
  sanitizeZapierData,
  triggerEvent,
} from "../../../utils/zapierUtils";
import { composeNewService } from "../services/ServiceUtils";
import {
  formValidate,
  lotPrefixTakenAtSave,
  waitForFormValidation,
  getCustomHeaders,
  normalizeWebsiteUrl,
  setAccountSyncFailed,
  validateEmailUnicity,
} from "./AccountUtils";
import { composeNewChargeRule } from "./chargeRules/ChargeRuleUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

const createServices = async (services, account) => {
  if (services?.length > 0) {
    const results = await Promise.allSettled(
      services.map((item) => {
        const input = composeNewService(item, account);
        return API.graphql(graphqlOperation(createService, { input }));
      }),
    );

    // Log history for each successfully created service
    const successfulServices = results
      .filter((result) => result.status === "fulfilled")
      .map((result) => result.value.data.createService);

    for (const service of successfulServices) {
      const historyNote = {
        parentId: service.id,
        parentType: ResourceType.SERVICE,
        text: "Service Imported.",
      };
      logHistory(historyNote);
    }

    const resError = results.find((itm) => itm.status === "rejected");
    if (resError) throw resError.reason;

    return successfulServices;
  }
  return [];
};

const createChargeRules = async (chargeRules, services, account) => {
  if (chargeRules?.length > 0) {
    const results = await Promise.allSettled(
      chargeRules.map((item) => {
        const input = composeNewChargeRule(item, services, account);
        return API.graphql(graphqlOperation(createChargeRule, { input }));
      }),
    );

    // Log history for each successfully created service
    const successfulChargeRules = results
      .filter((result) => result.status === "fulfilled")
      .map((result) => result.value.data.createChargeRule);

    for (const chargeRule of successfulChargeRules) {
      const historyNote = {
        parentId: chargeRule.id,
        parentType: ResourceType.CHARGE_RULE,
        text: "Rule Imported.",
      };
      logHistory(historyNote);
    }

    const resError = results.find((itm) => itm.status === "rejected");
    if (resError) throw resError.reason;

    return successfulChargeRules;
  }
  return [];
};

const createProducts = async (products, account) => {
  if (products?.length > 0) {
    const unitEntries = Object.entries(UnitToColumn).filter(
      (e) => e[0] !== Unit.CAR && e[0] !== Unit.TL,
    );
    const customHeaders = getCustomHeaders(products[0]);

    const results = await Promise.allSettled(
      products.map((item) => {
        const input = {
          active: account.active,
          groups: account.groups,
          reloadYardId: account.reloadYardId,
          accountId: account.id,
          status: InventoryStatus.STORED, // used for inventories connection
        };

        input.productCode = item["productCode"];
        input.name = item["name"];
        input.reloadYardCommodityId = item.reloadYardCommodityId;
        input.category = item["category"] ? item["category"] : null;
        input.defaultUOM = item["defaultUOM"];
        input.lowInventoryThreshold = Number(item["lowInventoryThreshold"]); // Number parse to 0

        input.pcsHeight = parseFloat(item["pcsHeight"]);
        input.pcsWidth = parseFloat(item["pcsWidth"]);
        input.pcsLength = parseFloat(item["pcsLength"]);
        input.height = parseFloat(item["height"]);
        input.length = parseFloat(item["length"]);
        input.width = parseFloat(item["width"]);

        unitEntries.forEach(([unit, column]) => {
          input[column] = parseFloat(item[column]);
        });

        input.totalQuantityOnHand = 0;
        input.totalQuantityCommitted = 0;
        input.lowInventoryAlert = false;

        if (customHeaders.length > 0) {
          input.customFields = [];
          customHeaders.forEach((header) => {
            input.customFields.push(`${header}||${item[header]}`);
          });
        }

        return API.graphql(graphqlOperation(createProduct, { input }));
      }),
    );

    // Log history for each successfully created product
    const successfulProducts = results
      .filter((result) => result.status === "fulfilled")
      .map((result) => result.value.data.createProduct);

    for (const product of successfulProducts) {
      const historyNote = {
        parentId: product.id,
        parentType: ResourceType.PRODUCT,
        text: "Product Imported.",
      };
      logHistory(historyNote);
    }

    const resError = results.find((itm) => itm.status === "rejected");
    if (resError) throw resError.reason;
  }
};

const createSavedAddresses = async (addresses, account) => {
  if (addresses?.length > 0) {
    const results = await Promise.allSettled(
      addresses.map((addr) => {
        const input = { ...addr, accountId: account.id };
        delete input.id; // clean up list side-effects
        return API.graphql(graphqlOperation(createSavedAddress, { input }));
      }),
    );

    const resError = results.find((itm) => itm.status === "rejected");
    if (resError) throw resError.reason;
  }
};

export const ValidateCreateButton = ({
  label,
  allServices,
  allChargeRules,
  allProducts,
  basePath,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [isSyncEnabled, setIsSyncEnabled] = useState(
    getTriggerEnabled("accountCreated"),
  );

  useEffect(() => {
    const checkSyncEnabled = async () => {
      const triggerEnabled = await fetchTriggerEnabled("accountCreated");
      setIsSyncEnabled(triggerEnabled);
    };
    checkSyncEnabled();
  }, []);

  const handleValidateSave = async (formData) => {
    debugger;
    const warnings = await validateEmailUnicity(formData.email, formData);

    if (warnings) {
      setConfirmMessage(warnings.message);
      setOpen(true);
    } else handleSave();
  };

  const handleDialogClose = () => setOpen(false);
  const handleDialogConfirm = async () => await handleSave();

  const handleSave = async () => {
    setOpen(false);

    // Async field validators (lot prefix unicity) may still be running.
    await waitForFormValidation(form);
    if (form.getState().valid) await lotPrefixTakenAtSave(form);
    await formValidate(form, notify);

    props.handleSubmitWithRedirect();
  };

  const transform = async (data) => {
    setIsSaving(true);
    data = { ...data };
    debugger;

    try {
      data.active = AccountState.ACTIVE;
      data.status = AccountStatus.ACTIVE;
      data.reloadYardId = window.appUser?.reloadYardId;
      data.groups = window.appUser?.groups;
      data.externalId = " ";
      data.businessName = data.businessName.trim();
      data.displayName = data.displayName?.trim();
      data.email = data.email.trim().toLowerCase();
      if (data.address?.state)
        data.address.state = data.address.state.trim().toUpperCase();
      if (data.address?.zipCode)
        data.address.zipCode = data.address.zipCode.trim().toUpperCase();
      data.addressState = data.address?.state ? data.address.state.trim() : " ";
      delete data.address?.name;

      if (data.billingEmail)
        data.billingEmail = data.billingEmail.trim().toLowerCase() || null;

      if (data.skipInvoice) data.billingAddress = null;
      if (data.billingAddress?.state)
        data.billingAddress.state = data.billingAddress.state.toUpperCase();
      if (data.billingAddress?.zipCode)
        data.billingAddress.zipCode = data.billingAddress.zipCode.toUpperCase();

      if (data.inboundCustomFields?.length > 0) {
        data.inboundCustomFields.forEach(
          (itm) => (itm.id = itm.id ? itm.id : getCustomFieldId(itm.name)),
        );
      }
      if (data.outboundCustomFields?.length > 0) {
        data.outboundCustomFields.forEach(
          (itm) => (itm.id = itm.id ? itm.id : getCustomFieldId(itm.name)),
        );
      }
      if (data.inventoryCustomFields?.length > 0) {
        data.inventoryCustomFields.forEach(
          (itm) => (itm.id = itm.id ? itm.id : getCustomFieldId(itm.name)),
        );
      }
      if (data.invoiceCustomFields?.length > 0) {
        data.invoiceCustomFields.forEach(
          (itm) => (itm.id = itm.id ? itm.id : getCustomFieldId(itm.name)),
        );
      }

      data.inboundPhotos = data.inboundPhotos || 0;
      data.secondaryReview = data.secondaryReview ? true : false;
      data.lotType = data.lotType ? data.lotType : "";
      data.lotNumberOverride = data.lotNumberOverride ? true : false;

      data.outboundPhotos = data.outboundPhotos || 0;
      data.inventoryPhotos = data.inventoryPhotos || 0;

      await Promise.all([
        saveRemoveFile(
          undefined,
          data,
          "bolTemplate",
          `${FilePaths.ACCOUNT}/${data.id}`,
        ),
        saveRemoveFile(
          undefined,
          data,
          "receiptTemplate",
          `${FilePaths.ACCOUNT}/${data.id}`,
        ),
        saveRemoveFiles(
          undefined,
          data,
          "attachments",
          `${FilePaths.ACCOUNT}/${data.id}`,
        ),
      ]);
    } catch (err) {
      logError("accounts:ValidateCreateButton:transform", err, notify);
      setIsSaving(false);
      return Promise.reject();
    }

    if (isSyncEnabled) {
      data.externalStatus = ExternalStatus.SYNC_PENDING;
    }

    // create form side-effect cleanup
    delete data.servicesFile;
    delete data.chargeRulesFile;
    delete data.productFile;

    const sanitized = sanitizeAccount(data);
    return sanitized;
  };

  const onSuccess = async ({ data }) => {
    debugger;
    try {
      const historyNote = {
        parentId: data.id,
        parentType: ResourceType.ACCOUNT,
        text: "Account Created",
      };
      logHistory(historyNote);

      const addressBookState = form.getFieldState("savedAddresses.items");
      const addresses = addressBookState?.value;

      // process files
      const attachmentsState = form.getFieldState("attachments");
      data.attachments = attachmentsState?.value;
      const bolState = form.getFieldState("bolTemplate");
      data.bolTemplate = bolState?.value;
      const receiptState = form.getFieldState("receiptTemplate");
      data.receiptTemplate = receiptState?.value;

      const newServices = await createServices(allServices, data);
      await createChargeRules(allChargeRules, newServices, data);
      await createProducts(allProducts, data);
      await createSavedAddresses(addresses, data);

      if (isSyncEnabled) {
        try {
          const sanitizedData = sanitizeZapierData(
            syncableAccountCreateProps,
            data,
          );
          const normalizedData = {
            ...sanitizedData,
            website: normalizeWebsiteUrl(sanitizedData.website),
          };
          // for create accounts from external systems.
          triggerEvent(normalizedData, "accountCreated").catch((error) => {
            logError("accounts:ValidateCreateButton:triggerEvent", error);
            setAccountSyncFailed(data);
          });
        } catch (error) {
          logError(
            "accounts:ValidateCreateButton:sanitizeZapierData",
            error,
            notify,
          );
          setAccountSyncFailed(data);
        }
      }

      notify("Account created successfully", "success");
      redirect("list", basePath);
    } catch (error) {
      logError(
        "accounts:ValidateCreateButton:onSuccess",
        error,
        notify,
        redirect,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError("accounts:ValidateCreateButton:onFailure", error, notify);
  };

  // Pass ...props, not ...rest to FormDataConsumer to include handleSubmitWithRedirect from props
  return (
    <FormDataConsumer>
      {({ formData, ...props }) => (
        <>
          <Backdrop className={classes.backdrop} open={isSaving}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <SaveButton
            label={label}
            icon={isSyncEnabled ? <UploadIcon /> : <SaveIcon />}
            submitOnEnter={false}
            undoable={false}
            saving={isSaving}
            handleSubmitWithRedirect={() => handleValidateSave(formData)}
            transform={transform}
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
          <Confirm
            isOpen={open}
            loading={isSaving}
            title="Save Confirmation"
            content={confirmMessage}
            onConfirm={handleDialogConfirm}
            onClose={handleDialogClose}
          />
        </>
      )}
    </FormDataConsumer>
  );
};
